import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Check, ArrowRight, ShieldCheck } from "lucide-react";
import axios from "axios";
import { useLang } from "@/lib/lang";
import { usePlacesAutocomplete, getDirections } from "../../../lib/googleMaps";
import { API } from "./constants";

/**
 * Public booking modal — 2-step flow:
 *   1. form with route + passenger + payment; live price preview via Google Directions + backend quote
 *   2. success screen with reference, awaiting-confirmation notice and tracking link
 * Accepts optional `prefill` (client name/email/phone/payment/route/datetime) for logged-in clients.
 */
export default function BookingModal({ categoryKey, fleet, partnerCtx, onClose, prefill }) {
  const { t } = useLang();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    pickup_address: prefill?.pickup_address || "",
    destination_address: prefill?.destination_address || "",
    service_datetime: prefill?.service_datetime || "",
    passengers: prefill?.passengers || 2,
    luggage: prefill?.luggage || 1,
    vehicle_type: categoryKey || "executive",
    client_name: prefill?.client_name || "",
    client_email: prefill?.client_email || "",
    client_phone: prefill?.client_phone || "",
    payment_method: prefill?.payment_method || "mbway",
    flight_number: "",
    special_requests: "",
    pickup_place_id: prefill?.pickup_place_id,
    pickup_lat: prefill?.pickup_lat,
    pickup_lng: prefill?.pickup_lng,
    destination_place_id: prefill?.destination_place_id,
    destination_lat: prefill?.destination_lat,
    destination_lng: prefill?.destination_lng,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [route, setRoute] = useState(null);
  const [priceQuote, setPriceQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const pickupRef = useRef(null);
  const destRef = useRef(null);

  const parseCapMax = (val, fallback) => {
    if (val == null) return fallback;
    if (typeof val === "number") return val;
    const nums = String(val).match(/\d+/g);
    if (!nums || nums.length === 0) return fallback;
    return Number(nums[nums.length - 1]);
  };
  const selectedCategory = (fleet || []).find((c) => c.key === form.vehicle_type);
  const maxPax = parseCapMax(
    selectedCategory?.capacity_passengers ?? selectedCategory?.sample_vehicle?.capacity_passengers,
    8
  );
  const maxBags = parseCapMax(
    selectedCategory?.capacity_bags ?? selectedCategory?.sample_vehicle?.capacity_luggage,
    8
  );

  // Clamp passengers/luggage when the category changes.
  useEffect(() => {
    setForm((s) => {
      const nextPax = Math.min(Number(s.passengers) || 1, maxPax);
      const nextBags = Math.min(Number(s.luggage) || 0, maxBags);
      if (nextPax === s.passengers && nextBags === s.luggage) return s;
      return { ...s, passengers: nextPax, luggage: nextBags };
    });
  }, [maxPax, maxBags]);

  // Wire Google Places Autocomplete on the two address inputs.
  usePlacesAutocomplete(pickupRef, (place) =>
    setForm((s) => ({
      ...s,
      pickup_address: place.formatted_address,
      pickup_place_id: place.place_id,
      pickup_lat: place.lat,
      pickup_lng: place.lng,
    }))
  );
  usePlacesAutocomplete(destRef, (place) =>
    setForm((s) => ({
      ...s,
      destination_address: place.formatted_address,
      destination_place_id: place.place_id,
      destination_lat: place.lat,
      destination_lng: place.lng,
    }))
  );

  // Fetch directions + live price whenever origin/dest/vehicle/time change.
  useEffect(() => {
    const hasOrigin = form.pickup_place_id || (form.pickup_lat && form.pickup_lng);
    const hasDest = form.destination_place_id || (form.destination_lat && form.destination_lng);
    if (!hasOrigin || !hasDest) {
      setRoute(null);
      setPriceQuote(null);
      return;
    }
    let cancel = false;
    setQuoting(true);
    getDirections(
      { place_id: form.pickup_place_id, lat: form.pickup_lat, lng: form.pickup_lng, address: form.pickup_address },
      { place_id: form.destination_place_id, lat: form.destination_lat, lng: form.destination_lng, address: form.destination_address }
    )
      .then(async (r) => {
        if (cancel) return;
        setRoute(r);
        const quote = await axios
          .post(`${API}/public/transfers/quote-dynamic`, {
            distance_km: r.distance_km,
            duration_min: r.duration_min,
            vehicle_type: form.vehicle_type,
            service_datetime: form.service_datetime ? new Date(form.service_datetime).toISOString() : null,
            partner_slug: partnerCtx?.slug || null,
          })
          .catch(() => ({ data: null }));
        if (!cancel) setPriceQuote(quote.data);
      })
      .catch(() => {
        if (!cancel) {
          setRoute(null);
          setPriceQuote(null);
        }
      })
      .finally(() => {
        if (!cancel) setQuoting(false);
      });
    return () => {
      cancel = true;
    };
  }, [
    form.pickup_place_id,
    form.destination_place_id,
    form.pickup_lat,
    form.pickup_lng,
    form.destination_lat,
    form.destination_lng,
    form.vehicle_type,
    form.service_datetime,
    partnerCtx?.slug,
  ]);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        type: "transfer",
        passengers: Number(form.passengers) || 1,
        luggage: Number(form.luggage) || 0,
        service_datetime: new Date(form.service_datetime).toISOString(),
        partner_slug: partnerCtx?.slug || null,
        distance_km: route?.distance_km || null,
        duration_min: route?.duration_min || null,
      };
      const r = await axios.post(`${API}/public/bookings`, payload);
      setResult(r.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || t("booking_error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const minDateTime = new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 16);

  return (
    <motion.div
      className="dt-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="booking-modal"
    >
      <motion.div
        className="dt-modal__box"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="dt-modal__close"
          onClick={onClose}
          aria-label={t("booking_close_label")}
          data-testid="booking-close"
        >
          <X size={20} />
        </button>

        {step === 1 && (
          <>
            <div className="dt-modal__header">
              <p className="dt-section__eyebrow">{t("booking_eyebrow")}</p>
              <h3 className="dt-modal__title">{t("booking_title")}</h3>
              {partnerCtx && (
                <p className="dt-modal__partner">
                  <ShieldCheck size={14} /> {t("booking_partner_prefix")}{" "}
                  <strong>{partnerCtx.company_name}</strong>
                </p>
              )}
            </div>
            <form className="dt-form" onSubmit={submit}>
              <div className="dt-form__row">
                <label className="dt-form__field">
                  <span>{t("booking_field_origin")}</span>
                  <input
                    ref={pickupRef}
                    required
                    value={form.pickup_address}
                    onChange={update("pickup_address")}
                    placeholder={t("booking_placeholder_pickup")}
                    data-testid="booking-pickup"
                    autoComplete="off"
                  />
                </label>
                <label className="dt-form__field">
                  <span>{t("booking_field_destination")}</span>
                  <input
                    ref={destRef}
                    required
                    value={form.destination_address}
                    onChange={update("destination_address")}
                    placeholder={t("booking_placeholder_destination")}
                    data-testid="booking-destination"
                    autoComplete="off"
                  />
                </label>
              </div>
              <div className="dt-form__row">
                <label className="dt-form__field">
                  <span>{t("booking_field_datetime")}</span>
                  <input
                    required
                    type="datetime-local"
                    min={minDateTime}
                    value={form.service_datetime}
                    onChange={update("service_datetime")}
                    data-testid="booking-datetime"
                  />
                </label>
                <label className="dt-form__field">
                  <span>{t("booking_field_category")}</span>
                  <select value={form.vehicle_type} onChange={update("vehicle_type")} data-testid="booking-category">
                    {fleet.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label} — {t("booking_category_up_to")} {c.capacity_passengers || 4} {t("landing_vehicle_pax")}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="dt-form__row">
                <label className="dt-form__field">
                  <span>
                    {t("booking_field_passengers")}{" "}
                    <small style={{ color: "var(--dt-muted)", fontWeight: 400 }}>
                      ({t("booking_max")} {maxPax})
                    </small>
                  </span>
                  <input
                    type="number"
                    min="1"
                    max={maxPax}
                    value={form.passengers}
                    onChange={(e) => {
                      const v = Math.max(1, Math.min(maxPax, Number(e.target.value) || 1));
                      setForm((s) => ({ ...s, passengers: v }));
                    }}
                    data-testid="booking-pax"
                  />
                </label>
                <label className="dt-form__field">
                  <span>
                    {t("booking_field_luggage")}{" "}
                    <small style={{ color: "var(--dt-muted)", fontWeight: 400 }}>
                      ({t("booking_max")} {maxBags})
                    </small>
                  </span>
                  <input
                    type="number"
                    min="0"
                    max={maxBags}
                    value={form.luggage}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(maxBags, Number(e.target.value) || 0));
                      setForm((s) => ({ ...s, luggage: v }));
                    }}
                    data-testid="booking-luggage"
                  />
                </label>
                <label className="dt-form__field">
                  <span>{t("booking_field_flight")}</span>
                  <input
                    value={form.flight_number}
                    onChange={update("flight_number")}
                    placeholder={t("booking_placeholder_flight")}
                    data-testid="booking-flight"
                  />
                </label>
              </div>

              {/* Live price preview */}
              {(route || quoting) && (
                <div className="dt-price-preview" data-testid="booking-price-preview">
                  {quoting && !priceQuote ? (
                    <div className="dt-price-loading">{t("booking_price_calculating")}</div>
                  ) : priceQuote?.enabled === false ? null : priceQuote ? (
                    <PricePreviewCard t={t} priceQuote={priceQuote} route={route} />
                  ) : null}
                </div>
              )}

              <hr className="dt-form__sep" />
              <div className="dt-form__row">
                <label className="dt-form__field">
                  <span>{t("booking_field_name")}</span>
                  <input required value={form.client_name} onChange={update("client_name")} data-testid="booking-name" />
                </label>
                <label className="dt-form__field">
                  <span>{t("booking_field_email")}</span>
                  <input required type="email" value={form.client_email} onChange={update("client_email")} data-testid="booking-email" />
                </label>
                <label className="dt-form__field">
                  <span>{t("booking_field_phone")}</span>
                  <input
                    required
                    type="tel"
                    value={form.client_phone}
                    onChange={update("client_phone")}
                    placeholder={t("booking_placeholder_phone")}
                    data-testid="booking-phone"
                  />
                </label>
              </div>
              <label className="dt-form__field">
                <span>{t("booking_field_notes")}</span>
                <textarea rows="2" value={form.special_requests} onChange={update("special_requests")} data-testid="booking-notes" />
              </label>
              <div className="dt-form__row">
                <label className="dt-form__field">
                  <span>{t("booking_field_payment")}</span>
                  <div className="dt-radio-group" data-testid="booking-payment-methods">
                    {[
                      { v: "mbway", labelKey: "booking_pm_mbway" },
                      { v: "multibanco", labelKey: "booking_pm_multibanco" },
                      { v: "card", labelKey: "booking_pm_card" },
                      { v: "apple_pay", labelKey: "booking_pm_apple" },
                      { v: "google_pay", labelKey: "booking_pm_google" },
                      { v: "pix", labelKey: "booking_pm_pix" },
                    ].map((opt) => (
                      <label key={opt.v} className={`dt-radio ${form.payment_method === opt.v ? "is-active" : ""}`}>
                        <input
                          type="radio"
                          name="pm"
                          value={opt.v}
                          checked={form.payment_method === opt.v}
                          onChange={(e) => setForm((s) => ({ ...s, payment_method: e.target.value }))}
                          data-testid={`booking-pm-${opt.v}`}
                        />
                        {t(opt.labelKey)}
                      </label>
                    ))}
                  </div>
                </label>
              </div>
              {error && (
                <div className="dt-form__error" data-testid="booking-error">
                  {error}
                </div>
              )}
              <div className="dt-modal__footer">
                <button type="button" className="dt-btn dt-btn--ghost" onClick={onClose}>
                  {t("booking_action_cancel")}
                </button>
                <button type="submit" className="dt-btn dt-btn--primary" disabled={submitting} data-testid="booking-submit">
                  {submitting ? t("booking_action_processing") : (<>{t("booking_action_confirm")} <ArrowRight size={14} /></>)}
                </button>
              </div>
            </form>
          </>
        )}

        {step === 2 && result && (
          <div className="dt-success" data-testid="booking-success">
            <div className="dt-success__badge">
              <Check size={32} />
            </div>
            <h3 className="dt-modal__title">{t("booking_success_title")}</h3>
            <p className="dt-success__ref">
              {t("booking_success_reference")}{" "}
              <strong data-testid="booking-reference">{result.reference}</strong>
            </p>

            {result.awaiting_confirmation && (
              <div className="dt-await" data-testid="booking-awaiting">
                <div className="dt-await__title">{t("booking_awaiting_title")}</div>
                <p className="dt-await__body">{t("booking_awaiting_body")}</p>
              </div>
            )}

            <div className="dt-success__total">
              {t("booking_success_total")} <strong>€{Number(result.client_pays).toFixed(2)}</strong>
            </div>

            <div className="dt-success__track" data-testid="booking-tracking-info">
              <p className="dt-payment__title" style={{ marginTop: 0 }}>
                {t("booking_tracking_title")}
              </p>
              <Link
                to={`/booking/${result.reference}?code=${result.security_code}`}
                className="dt-btn dt-btn--ghost"
                data-testid="booking-tracking-link"
              >
                {t("booking_tracking_link")} <ArrowRight size={14} />
              </Link>
              <p style={{ color: "var(--dt-muted)", fontSize: "0.75rem", marginTop: "0.5rem" }}>
                {t("booking_tracking_note")}
              </p>
            </div>

            <div className="dt-modal__footer">
              <button type="button" className="dt-btn dt-btn--primary" onClick={onClose} data-testid="booking-done">
                {t("booking_action_done")}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

/**
 * Dark-navy pricing card with total in gold + KM/duration + surcharge chips.
 * Kept in the same file to avoid over-fragmentation — used only by BookingModal.
 */
function PricePreviewCard({ t, priceQuote, route }) {
  return (
    <div
      data-testid="booking-price-card"
      style={{
        background: "linear-gradient(135deg, #0a0f1e 0%, #1a2036 100%)",
        color: "#fff",
        padding: "1.25rem 1.5rem",
        border: "2px solid #f59e0b",
        borderRadius: 0,
        marginTop: "0.5rem",
      }}
    >
      <div
        style={{
          fontSize: "0.65rem",
          textTransform: "uppercase",
          letterSpacing: "0.25em",
          color: "#f59e0b",
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        {t("booking_price_estimated_total")}
      </div>
      <div
        data-testid="booking-price-total"
        style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "3rem",
          lineHeight: 1,
          fontWeight: 700,
          color: "#f59e0b",
        }}
      >
        €{Number(priceQuote.total_eur).toFixed(2)}
      </div>

      {route && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
            marginTop: "1rem",
            paddingTop: "1rem",
            borderTop: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          <div>
            <div style={{ fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.2em", color: "rgba(255,255,255,0.6)", fontWeight: 700 }}>
              {t("booking_price_distance")}
            </div>
            <div
              data-testid="booking-price-distance"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.4rem", fontWeight: 700, marginTop: 2 }}
            >
              {route.distance_km.toFixed(1)}
              <span style={{ fontSize: "0.85rem", opacity: 0.7, marginLeft: 4 }}>km</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.2em", color: "rgba(255,255,255,0.6)", fontWeight: 700 }}>
              {t("booking_price_duration")}
            </div>
            <div
              data-testid="booking-price-duration"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.4rem", fontWeight: 700, marginTop: 2 }}
            >
              {(() => {
                const m = Math.round(route.duration_min);
                if (m < 60) return <>{m}<span style={{ fontSize: "0.85rem", opacity: 0.7, marginLeft: 4 }}>{t("booking_price_min")}</span></>;
                const h = Math.floor(m / 60);
                const r = m % 60;
                return <>{h}h {r ? `${r}${t("booking_price_min")}` : ""}</>;
              })()}
            </div>
          </div>
        </div>
      )}

      {(priceQuote.night_surcharge_pct > 0 || priceQuote.weekend_surcharge_pct > 0 || priceQuote.partner_markup_eur > 0) && (
        <div style={{ marginTop: "0.75rem", fontSize: "0.72rem", color: "rgba(255,255,255,0.7)", display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
          {priceQuote.night_surcharge_pct > 0 && <span>🌙 +{priceQuote.night_surcharge_pct}% {t("booking_surcharge_night")}</span>}
          {priceQuote.weekend_surcharge_pct > 0 && <span>📅 +{priceQuote.weekend_surcharge_pct}% {t("booking_surcharge_weekend")}</span>}
          {priceQuote.partner_markup_eur > 0 && <span>🏨 {t("booking_surcharge_partner")}: €{priceQuote.partner_markup_eur.toFixed(2)}</span>}
        </div>
      )}
    </div>
  );
}
